import axios from "axios";
import "firebase/auth";
import {
  appVersion,
  clearCookies,
  clearStorages,
  getFirebaseUser,
  getStorage,
} from "../auth/utils";
import { DateTime } from "luxon";
import { LOGIN_ROUTE } from "../routes";
import { storeFirebaseToken } from "../auth/firebaseSession";
import { isCoreRequest } from "./coreRequest";

let refreshPromise = null;
let refreshIdentityUid = null;

axios.interceptors.request.use(
  async (config) => {
    if (!isCoreRequest(config)) return config;

    // Ensure headers exist
    config.headers = config.headers || {};

    // Public and authenticated Core requests must select the same release.
    config.headers["x-app-version"] = appVersion;

    // Add active club header for multi-club context
    const activeClubId = getStorage().getItem("activeClubId");
    if (activeClubId && !config.withoutClub) {
      config.headers["x-club-id"] = String(activeClubId);
    }
    if (config.withoutClub) delete config.headers["x-club-id"];
    if (config.expectedFirebaseUid && getStorage().getItem("firebaseUid") !== config.expectedFirebaseUid) {
      throw new Error("session_identity_changed");
    }

    const requiresAuth = config.headers["x-requires-auth"] === "true";

    if (!requiresAuth) {
      return config;
    }

    // AUTH LOGIC BELOW (only for authenticated calls)
    config.authFirebaseUid = getStorage().getItem("firebaseUid");

    let token = config.sessionToken || getStorage().getItem("token");
    const tokenLastValidity = getStorage().getItem("tokenLastValidity");
    const tokenExpired =
      tokenLastValidity &&
      DateTime.fromMillis(+tokenLastValidity).plus({ minutes: 30 }).toMillis() <
        DateTime.now().toMillis();

    if (!config.sessionToken && (!token || tokenExpired)) {
      const requestUid = getStorage().getItem("firebaseUid");
      if (!refreshPromise || refreshIdentityUid !== requestUid) {
        refreshIdentityUid = requestUid;
        refreshPromise = getFirebaseUser().then(async (user) => {
          if (!user) throw new Error("No Firebase user");
          const newToken = await user.getIdToken(true);
          if (getStorage().getItem("firebaseUid") !== requestUid ||
            (requestUid && user.uid !== requestUid)) throw new Error("session_identity_changed");
          storeFirebaseToken(user, newToken);
          return newToken;
        });
      }
      const pendingRefresh = refreshPromise;
      try {
        token = await pendingRefresh;
      } catch (error) {
        if (error?.message === "session_identity_changed" || getStorage().getItem("firebaseUid") !== requestUid) throw error;
        clearStorages(["token", "user", "refreshToken", "tokenLastValidity", "firebaseUid", "impersonate", "activeClubId", "preImpersonationActiveClubId"]);
        clearCookies();
        window.location.href = LOGIN_ROUTE;
        throw new Error("Session expired. Redirecting to login.");
      } finally {
        if (refreshPromise === pendingRefresh) refreshPromise = null;
      }
    }

    if (config.expectedFirebaseUid && getStorage().getItem("firebaseUid") !== config.expectedFirebaseUid) {
      throw new Error("session_identity_changed");
    }
    config.headers.Authorization = `Bearer ${token}`;

    // Impersonation support
    const impersonate = getStorage().getItem("impersonate");
    if (impersonate) {
      try {
        const impersonatedUser = JSON.parse(impersonate);
        const impersonateIdentity =
          impersonatedUser?.email || impersonatedUser?.mobile;

        if (impersonateIdentity) {
          config.headers.impersonate = impersonateIdentity;
        }
      } catch (error) {
        // Fail safely: malformed local impersonation state should not break all requests.
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  function (response) {
    if (!isCoreRequest(response?.config)) return response;

    const requestUid = response?.config?.expectedFirebaseUid || response?.config?.authFirebaseUid;
    if (requestUid && requestUid !== getStorage().getItem("firebaseUid")) {
      return Promise.reject(new Error("session_identity_changed"));
    }
    return response;
  },
  function (error) {
    if (!isCoreRequest(error?.config)) return Promise.reject(error);

    const requestUid = error?.config?.expectedFirebaseUid || error?.config?.authFirebaseUid;
    const belongsToCurrentIdentity = !requestUid || requestUid === getStorage().getItem("firebaseUid");
    if (error?.response?.status === 401 && belongsToCurrentIdentity) {
      getStorage().removeItem("token");
      getStorage().removeItem("refreshToken");
      window.location.href = LOGIN_ROUTE;
    }

    return Promise.reject(error);
  }
);

const baseUrl = process.env.REACT_APP_BASE_URL;

export function axiosGraphQL(payload, { noAuth = false, withoutClub = false, sessionToken, expectedFirebaseUid } = {}) {
  return axios.post(`${baseUrl}/graphql`, payload, {
    withoutClub,
    sessionToken,
    expectedFirebaseUid,
    headers: {
      ...(noAuth ? {} : { "x-requires-auth": "true" }), // tells interceptor this request needs auth
    },
  });
}

export function axiosGet(url) {
  return axios.get(`${baseUrl}${url}`);
}

export function axiosPost(url, payload) {
  return axios.post(`${baseUrl}${url}`, payload);
}

export function axiosAuthenticatedPost(url, payload, { expectedFirebaseUid } = {}) {
  return axios.post(`${baseUrl}${url}`, payload, {
    expectedFirebaseUid,
    headers: { "x-requires-auth": "true" },
  });
}

export function axiosAuthenticatedRawPost(url, body, contentType, { expectedFirebaseUid, withoutClub = false } = {}) {
  return axios.post(`${baseUrl}${url}`, body, {
    expectedFirebaseUid,
    withoutClub,
    headers: {
      "x-requires-auth": "true",
      "Content-Type": contentType,
    },
    transformRequest: [(data) => data],
  });
}
